import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Flex, Input, Select, Space, message, Switch, Table, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { getProducts, updateProductStatus } from "../../services/productService";
import ProductResponse from "../../payload/response/ProductResponse";
import ServiceResponse from "../../payload/response/ServiceResponse";
import { EditFilled, SearchOutlined } from "@ant-design/icons";
import ProductImage from "../../components/ProtectedImage/ProtectedImage";
import { useState, useEffect } from "react";
import SelectManufacturer from "../../components/SelectManufacturer/SelectManufacturer";
import SelectCategory from "../../components/SelectCategory/SelectCategory";
const { Title } = Typography;
export default function Products() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    name: "",
    activeSubstance: "",
    manufacturerId: undefined as number | undefined,
    categoryId: undefined as number | undefined,
    isActive: undefined as boolean | undefined,
  });
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [productsData, setProductsData] = useState<ServiceResponse<ProductResponse> | null>(null);

  const [sort, setSort] = useState<{ sortField?: string; sortDirection?: string }>({
    sortField: undefined,
    sortDirection: undefined,
  });

  const { mutate: fetchProducts, isPending: isLoading } = useMutation({
    mutationFn: () =>
      getProducts(pagination.current, pagination.pageSize, {
        name: filters.name.toLowerCase().trim() || undefined,
        activeSubstance: filters.activeSubstance.toLowerCase().trim() || undefined,
        manufacturerId: filters.manufacturerId,
        categoryId: filters.categoryId,
        isActive: filters.isActive,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      }),
    onSuccess: (data) => {
      setProductsData(data);
      if (data?.totalPage) {
        setPagination((prev) => ({
          ...prev,
          total: data.totalPage * pagination.pageSize,
        }));
      }
    },
    onError: (error) => {
      message.error("Ürünler yüklenirken bir hata oluştu");
    },
  });

  useEffect(() => {
    fetchProducts();
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (productsData?.totalPage) {
      setPagination((prev) => ({
        ...prev,
        total: productsData.totalPage * pagination.pageSize,
      }));
    }
  }, [productsData?.totalPage, pagination.pageSize]);

  const handleChangeProductStatus = useMutation({
    mutationFn: updateProductStatus,
    onSuccess: () => {
      fetchProducts();
      message.success("Ürün durumu güncellendi");
    },
    onError: (error) => {
      message.error("Ürün durumu güncellenirken bir hata oluştu");
    },
  });

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={2}>Ürünler</Title>
        <Button
          onClick={() => {
            navigate("/admin/products/add");
          }}
          type="primary"
        >
          Ürün Ekle
        </Button>
      </Flex>
      <Space size={8} style={{ marginBottom: 16, width: "100%" }} wrap>
        <Input
          placeholder="Ürün Adı ara"
          allowClear
          value={filters.name}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, name: e.target.value }));
          }}
          style={{ width: 280 }}
        />
        <Input
          placeholder="Etken Madde ara"
          allowClear
          value={filters.activeSubstance}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, activeSubstance: e.target.value }));
          }}
          style={{ width: 280 }}
        />
        <SelectManufacturer
          value={selectedManufacturerId}
          onChange={(value) => {
            setSelectedManufacturerId(value);
            setFilters((prev) => ({
              ...prev,
              manufacturerId: value,
            }));
          }}
          style={{ width: 400 }}
        />
        <SelectCategory
          value={selectedCategoryId}
          onChange={(value) => {
            setSelectedCategoryId(value);
            setFilters((prev) => ({
              ...prev,
              categoryId: value,
            }));
          }}
          showRootCategory={false}
          isOnlySelectSub={true}
          style={{ width: 400 }}
        />
        <Select
          placeholder="Durum"
          allowClear
          style={{ width: 280 }}
          value={filters.isActive !== undefined ? String(filters.isActive) : undefined}
          onChange={(val) => {
            setFilters((prev) => ({
              ...prev,
              isActive: val !== undefined ? val === "true" : undefined,
            }));
          }}
          options={[
            { label: "Aktif", value: "true" },
            { label: "Pasif", value: "false" },
          ]}
        />
        <Select
          placeholder="Sırala"
          allowClear
          style={{ width: 280 }}
          value={sort.sortField && sort.sortDirection ? `${sort.sortField}_${sort.sortDirection}` : undefined}
          onChange={(val) => {
            if (!val) {
              setSort({ sortField: undefined, sortDirection: undefined });
              return;
            }
            const [field, direction] = val.split("_");
            setSort({ sortField: field, sortDirection: direction });
          }}
          options={[
            { label: "Ürün Adı A-Z", value: "name_asc" },
            { label: "Ürün Adı Z-A", value: "name_desc" },
            { label: "Kategori A-Z", value: "categoryName_asc" },
            { label: "Kategori Z-A", value: "categoryName_desc" },
          ]}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => {
            setPagination((prev) => ({ ...prev, current: 0 }));
            fetchProducts();
          }}
          loading={isLoading}
        >
          Ara
        </Button>
        <Button
          onClick={() => {
            setFilters({ name: "", activeSubstance: "", manufacturerId: undefined, categoryId: undefined, isActive: undefined });
            setSelectedManufacturerId(undefined);
            setSelectedCategoryId(undefined);
            setSort({ sortField: undefined, sortDirection: undefined });
            setPagination((prev) => ({ ...prev, current: 0 }));
            fetchProducts();
          }}
        >
          Temizle
        </Button>
      </Space>
      <Table<ProductResponse>
        dataSource={productsData?.list?.map?.((item) => ({ ...item, key: item.id }))}
        loading={isLoading}
        scroll={{ x: 1500 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize,
            }));
          },
        }}
        columns={[
          {
            title: "Resim",
            dataIndex: "images",
            key: "images",
            render: (_, record) => {
              return (
                <ProductImage
                  imageUrl={record?.images?.[0]?.imageUrl || "error"}
                  name={record.name}
                />
              );
            },
          },
          {
            title: "Ürün Adı",
            dataIndex: "name",
            key: "name",
            render: (name) => <span>{name}</span>,
          },
          {
            title: "Etken Madde",
            dataIndex: "activeSubstance",
            key: "activeSubstance",
            render: (_, record) => <span>{record.activeSubstance}</span>,
          },
          {
            title: "Üretici Firma",
            dataIndex: "manufacturerName",
            key: "manufacturerName",
            render: (_, record) => <span>{record.manufacturer.name}</span>,
          },
          {
            title: "Kategori Adı",
            dataIndex: "categoryName",
            key: "categoryId",
          },
          {
            title: "Durum",
            dataIndex: "isActive",
            key: "isActive",
            render(value, record, index) {
              return (
                <Switch
                  checkedChildren="Aktif"
                  unCheckedChildren="Pasif"
                  loading={handleChangeProductStatus.isPending}
                  onChange={(value) => {
                    handleChangeProductStatus.mutate(record.id);
                  }}
                  checked={record.isActive}
                />
              );
            },
          },
          {
            title: "İşlem",
            key: "action",
            render: (text, record) => (
              <Button
                type="primary"
                onClick={() => {
                  navigate(`/admin/products/edit/${record.id}`);
                }}
              >
                <EditFilled />
              </Button>
            ),
          },
        ]}
      />
      {productsData?.count !== undefined && (
        <Typography.Text style={{ marginTop: 16, display: "block" }}>
          Toplam bulunan kayıt sayısı: <strong>{productsData.count}</strong>
        </Typography.Text>
      )}
    </>
  );
}
